// Dashboard.jsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { dashboardApi } from "../../../../services/dashboardApi";
import { validateReportDateRange } from "../../../common/util";
import useDashboardDetails from "./useDashboardDetails";

import DashboardHeader from "./components/DashboardHeader";
import MetricCard from "./components/MetricCard";
import SkeletonCard from "./components/SkeletonCard";

import ProductionDashboard from "./ProductionDashboard/ProductionDashboard";
// import OperatorUtilizationDashboard from "./operator-utilization/OperatorUtilizationDashboard";

import "./Dashboard.scss";
import MachineUtilizationDashboard from "./MachineUtilizationDashboard/MachineUtilizationDashboard";
import OperatorUtilizationDashboard from "./operator-utilization/OperatorUtilizationDashboard";

/*
 * Auto refresh interval:
 * 10 minutes = 2 * 60 * 1000 milliseconds
 */
// const DASHBOARD_REFRESH_INTERVAL = 40 * 60 * 1000;

/*
 * Default dashboard data structure.
 *
 * Defined outside the component so that the same object structure
 * can be reused without recreating it on every render.
 */
const INITIAL_DASHBOARD_DATA = {
  productionToday: null,
  machineUtilization: null,
  departmentsSummary: null,
  rejectionRate: null,
  operatorsSummary: null,
  activeJobCards: null,
};

/*
 * Get today's local date in YYYY-MM-DD format.
 */
const getTodayDate = () => {
  const date = new Date();

  const offsetDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  );

  return offsetDate.toISOString().split("T")[0];
};

/*
 * Safe number formatter.
 */
const formatNumber = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(Number(value))
  ) {
    return "0";
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value));
};

/*
 * Format date/time for dashboard header.
 */
const formatDateTime = (date) => {
  if (!date) {
    return "-";
  }

  try {
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch (error) {
    console.error("Date formatting error:", error);

    return "-";
  }
};

/*
 * Format only refresh time.
 */
// const formatRefreshTime = (date) => {
//   if (!date) {
//     return "-";
//   }

//   try {
//     return new Intl.DateTimeFormat("en-IN", {
//       hour: "2-digit",
//       minute: "2-digit",
//       second: "2-digit",
//       hour12: true,
//     }).format(date);
//   } catch (error) {
//     console.error("Refresh time formatting error:", error);

//     return "-";
//   }
// };

function Dashboard() {
  const today = getTodayDate();
  const filterDaysLimit = useSelector(
    (state) => state?.common?.data?.filterDaysLimit,
  );
  const detailsEnabled = useSelector(
    (state) => Boolean(state?.common?.data?.dashboardControl?.machine_operator_dashboard),
  );

  /*
   * Main dashboard filters.
   */
  const [filters, setFilters] = useState({
    fromDate: today,
    toDate: today,
    targetProduction: 700000,
  });

  /*
   * KPI/API data.
   */
  const [dashboardData, setDashboardData] = useState(INITIAL_DASHBOARD_DATA);

  const [detailFilters, setDetailFilters] = useState(filters);
  const [detailRefreshVersion, setDetailRefreshVersion] = useState(0);
  const [selectionVersion, setSelectionVersion] = useState(0);

  /*
   * Initial page loading.
   *
   * This should only be true during:
   * - first dashboard load
   * - filter change when explicitly doing full load
   */
  const [loading, setLoading] = useState(true);

  /*
   * Background refresh state.
   *
   * Existing dashboard stays visible while this is true.
   */
  const [isRefreshing, setIsRefreshing] = useState(false);

  /*
   * Last successful dashboard update.
   */
  const [lastUpdated, setLastUpdated] = useState(null);

  /*
   * Main dashboard error.
   */
  const [error, setError] = useState("");

  /*
   * Names of APIs that failed during latest refresh.
   */
  const [failedApiKeys, setFailedApiKeys] = useState([]);
  const [selectedCardName, setSelectedCardName] = useState("PRODUCTION");
  const activeCardName = detailsEnabled ? selectedCardName : "PRODUCTION";
  const details = useDashboardDetails({
    selectedCard: activeCardName,
    machineOperatorEnabled: detailsEnabled,
    filters: detailFilters,
    refreshVersion: detailRefreshVersion,
    selectionVersion,
  });
  const allFailedApiKeys = [...failedApiKeys, ...details.failedKeys];

  /*
   * Prevent duplicate simultaneous requests.
   *
   * Example:
   * - 5-minute auto refresh starts
   * - user immediately clicks Refresh
   *
   * Second request will be ignored until first finishes.
   */
  const isFetchingRef = useRef(false);

  /*
   * Determines whether dashboard has completed at least
   * one successful data load.
   */
  const hasLoadedOnceRef = useRef(false);

  /*
   * Keep latest filter values available to interval callback
   * without unnecessarily recreating interval.
   */
  const filtersRef = useRef(filters);

  /*
   * Keep latest filters synchronized.
   */
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  /*
   * Main dashboard fetch function.
   *
   * Parameters:
   *
   * silent:
   *   true  = background refresh, old data remains visible
   *   false = initial load with skeleton
   *
   * force:
   *   true = allows manual/filter refresh after required checks
   */
  const fetchDashboardData = useCallback(
    async ({ silent = false, force = false } = {}) => {
      /*
       * Prevent duplicate active fetch.
       */
      if (isFetchingRef.current && !force) {
        return;
      }

      /*
       * Even force should not create duplicate overlapping
       * requests because that can cause stale data race conditions.
       */
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;

      const currentFilters = filtersRef.current;

      /*
       * First page load:
       * show skeleton cards.
       *
       * Background auto-refresh:
       * keep old dashboard visible.
       */
      if (!hasLoadedOnceRef.current && !silent) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      setError("");
      setFailedApiKeys([]);

      /*
       * KPI API calls. Detailed views load separately when a card is opened.
       */
      const apiCalls = [
        {
          key: "productionToday",
          call: () => dashboardApi.getProductionToday(currentFilters),
        },
        {
          key: "machineUtilization",
          call: () => dashboardApi.getMachineUtilization(currentFilters),
        },
        {
          key: "departmentsSummary",
          call: () => dashboardApi.getDepartmentsSummary(),
        },
        {
          key: "rejectionRate",
          call: () => dashboardApi.getRejectionRate(currentFilters),
        },
        {
          key: "operatorsSummary",
          call: () => dashboardApi.getOperatorsSummary(currentFilters),
        },
        {
          key: "activeJobCards",
          call: () => dashboardApi.getActiveJobCards(currentFilters),
        },
      ];

      let successfulApiCount = 0;
      // let completedApiCount = 0;
      const failures = [];

      /*
       * Handles each API response immediately.
       * We only wait for all promises inside finally/cleanup.
       */
      const requestPromises = apiCalls.map(async (api) => {
        try {
          const response = await api.call();

          const data = response?.data !== undefined ? response.data : null;

          /*
           * Update the corresponding dashboard state immediately
           * when this individual API responds.
           */
          if (data !== null && data !== undefined) {
            setDashboardData((previousData) => ({
              ...previousData,
              [api.key]: data,
            }));

            successfulApiCount += 1;

            /*
             * As soon as the first API succeeds,
             * remove initial page skeleton and display available data.
             */
            if (!hasLoadedOnceRef.current) {
              hasLoadedOnceRef.current = true;
              setLoading(false);
            }

            setLastUpdated(new Date());
          }

          return {
            key: api.key,
            success: true,
            data,
          };
        } catch (apiError) {
          console.error(`${api.key} API Error:`, apiError);

          failures.push(api.key);

          /*
           * Update failed APIs immediately as well.
           */
          setFailedApiKeys((previousKeys) => {
            if (previousKeys.includes(api.key)) {
              return previousKeys;
            }

            return [...previousKeys, api.key];
          });

          return {
            key: api.key,
            success: false,
            error: apiError,
          };
        } finally {
          // completedApiCount += 1;

          /*
           * This API finished, but other APIs can continue loading.
           */
          // console.log(
          //   `${completedApiCount}/${apiCalls.length} dashboard APIs completed`,
          // );
        }
      });

      try {
        /*
         * Individual API data is already updated above.
         * This wait is used only to know when the complete refresh is finished.
         */
        await Promise.allSettled(requestPromises);

        if (failures.length === apiCalls.length) {
          setError(
            "Unable to refresh dashboard data. The latest available information is still being displayed.",
          );
        } else if (failures.length > 0) {
          console.warn("Some dashboard APIs failed:", failures);
        }

        /*
         * Handles successful responses where data may be null.
         */
        if (successfulApiCount > 0) {
          hasLoadedOnceRef.current = true;
        }
      } catch (dashboardError) {
        console.error("Dashboard API Error:", dashboardError);

        setError(
          "Unable to fetch dashboard data. Please check the API server.",
        );
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [],
  );

  /*
   * Initial dashboard loading.
   *
   * Runs once when component mounts.
   */
  useEffect(() => {
    fetchDashboardData({
      silent: false,
    });
  }, [fetchDashboardData]);

  /*
   * Refresh dashboard whenever filters change.
   *
   * Skip first render because initial-load effect above
   * already handles it.
   */
  // const isInitialFilterRenderRef = useRef(true);

  // useEffect(() => {
  //   if (isInitialFilterRenderRef.current) {
  //     isInitialFilterRenderRef.current = false;
  //     return;
  //   }

  //   fetchDashboardData({
  //     silent: true,
  //   });
  // }, [filters, fetchDashboardData]);

  /*
   * Auto refresh every 5 minutes.
   *
   * No page reload.
   * No card reset.
   * No chart disappearing.
   */
  // useEffect(() => {
  //   const intervalId = window.setInterval(() => {
  //     fetchDashboardData({
  //       silent: true,
  //     });
  //   }, DASHBOARD_REFRESH_INTERVAL);

  //   /*
  //    * Cleanup interval when component unmounts.
  //    */
  //   return () => {
  //     window.clearInterval(intervalId);
  //   };
  // }, [fetchDashboardData]);

  /*
   * Optional:
   *
   * Refresh immediately when user returns to browser tab
   * after leaving dashboard in background.
   *
   * This gives a more professional "live dashboard" behavior.
   */
  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === "visible" && hasLoadedOnceRef.current) {
  //       fetchDashboardData({
  //         silent: true,
  //       });
  //     }
  //   };

  //   document.addEventListener("visibilitychange", handleVisibilityChange);

  //   return () => {
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //   };
  // }, [fetchDashboardData]);

  /*
   * Manual refresh from DashboardHeader.
   */
  const handleManualRefresh = useCallback(() => {
    if (isFetchingRef.current) return;
    const currentFilters = filtersRef.current;
    if (
      !validateReportDateRange(
        currentFilters.fromDate,
        currentFilters.toDate,
        filterDaysLimit,
      )
    ) {
      return;
    }

    setDetailFilters(currentFilters);
    setDetailRefreshVersion((version) => version + 1);
    void fetchDashboardData({
      silent: true,
    });
  }, [fetchDashboardData, filterDaysLimit]);

  /*
   * Dashboard KPI cards.
   */
  const cards = useMemo(() => {
    const production = dashboardData.productionToday;
    const machine = dashboardData.machineUtilization;
    const department = dashboardData.departmentsSummary;
    const rejection = dashboardData.rejectionRate;
    const operators = dashboardData.operatorsSummary;
    const jobcards = dashboardData.activeJobCards;

    return [
      {
        key: "production",
        title: production?.title || "Production Today",
        icon: "📈",
        value: formatNumber(production?.value),
        unit: "",
        percentage: Number(production?.percentage) || 0,
        label: `Target: ${formatNumber(
          production?.target || filters.targetProduction,
        )} Units`,
        tone: "blue",
        info: "production-utilization",
        dataType: "PRODUCTION",
      },
      {
        key: "machine",
        title: machine?.title || "Machine Utilization",
        icon: "⏱️",
        value: formatNumber(machine?.value),
        unit: machine?.unit || "%",
        percentage: Number(machine?.value) || 0,
        label: machine?.label || "Average Utilization",
        tone: "green",
        info: machine?.averageMachineUtilizationFormula,
        dataType: "MACHINE",
      },
      {
        key: "department",
        title: department?.title || "Departments",
        icon: "🏢",
        value: formatNumber(department?.active),
        unit: department?.total ? `/ ${formatNumber(department.total)}` : "",
        percentage: Number(department?.percentage) || 0,
        label: department?.label || "Active Departments",
        tone: "purple",
        info: "",
        dataType: "",
      },
      {
        key: "rejection",
        title: rejection?.title || "Rejection Rate",
        icon: "🛡️",
        value: formatNumber(rejection?.percentage),
        unit: "%",
        percentage: Number(rejection?.percentage) || 0,
        label: `${formatNumber(rejection?.units)} Units`,
        tone: "red",
        danger: true,
        info: "",
        dataType: "",
      },
      {
        key: "operators",
        title: "Operators",
        icon: "👥",
        value: formatNumber(operators?.present),
        unit: operators?.total ? `/ ${formatNumber(operators.total)}` : "",
        percentage: Number(operators?.percentage) || 0,
        label: "Operators Present",
        tone: "orange",
        info: "",
        dataType: "OPERATOR",
      },
      {
        key: "jobcards",
        title: jobcards?.title || "Active Job Cards",
        icon: "📋",
        value: formatNumber(jobcards?.completed),
        unit: jobcards?.completed ? `/ ${formatNumber(jobcards?.total)}` : "",
        percentage: jobcards?.completedPercentage || 0,
        label: jobcards?.label || "Running Job Cards",
        tone: "cyan",
        info: "",
        active: jobcards?.active,
        dataType: "",
      },
    ];
  }, [dashboardData, filters.targetProduction]);

  /*
   * Initial loading condition.
   *
   * Skeleton only before first successful page load.
   */
  const showInitialSkeleton = loading && !hasLoadedOnceRef.current;

  const handleSelectedCard = (dataType) => {
    if (!["PRODUCTION", "MACHINE", "OPERATOR"].includes(dataType) ||
      (dataType !== "PRODUCTION" && !detailsEnabled)) {
      return;
    }
    const currentFilters = filtersRef.current;
    if (!validateReportDateRange(
      currentFilters.fromDate, currentFilters.toDate, filterDaysLimit,
    )) {
      return;
    }
    setDetailFilters(currentFilters);
    setSelectedCardName(dataType);
    setSelectionVersion((version) => version + 1);
  };
  return (
    <div className="dashboard-page dashboard main" id="main">
      {/* =========================
          DASHBOARD HEADER
      ========================== */}

      <DashboardHeader
        filters={filters}
        setFilters={setFilters}
        onRefresh={handleManualRefresh}
        loading={loading}
        isRefreshing={isRefreshing}
        lastUpdated={formatDateTime(lastUpdated)}
      />

      {/* =========================
          LIVE UPDATE STATUS
      ========================== */}

      {/* <section className="dashboard-live-status">
        <div className="dashboard-live-status-left">
          <span
            className={`dashboard-live-dot ${isRefreshing ? "refreshing" : ""}`}
          />

          <div>
            <strong>
              {isRefreshing
                ? "Refreshing live production data"
                : "Live dashboard connected"}
            </strong>

            <small>
              {isRefreshing
                ? "Updating cards and charts without reloading the page."
                : `Automatic refresh every 5 minutes${
                    lastUpdated
                      ? ` • Last updated at ${formatRefreshTime(lastUpdated)}`
                      : ""
                  }`}
            </small>
          </div>
        </div>

        <div className="dashboard-live-status-right">
          {isRefreshing && <span className="dashboard-refresh-spinner" />}

          <span
            className={`dashboard-refresh-state ${
              isRefreshing ? "is-refreshing" : "is-live"
            }`}
          >
            {isRefreshing ? "Updating" : "Live"}
          </span>
        </div>
      </section> */}

      {/* =========================
          FULL API ERROR
      ========================== */}

      {error && (
        <div className="dashboard-alert">
          <strong>API Error:</strong>

          <span>{error}</span>

          <button
            type="button"
            disabled={loading || isRefreshing}
            onClick={handleManualRefresh}
          >
            Retry
          </button>
        </div>
      )}

      {/* =========================
          PARTIAL API WARNING
      ========================== */}

      {!error && allFailedApiKeys.length > 0 && (
        <div className="dashboard-partial-warning">
          <span className="dashboard-warning-icon">!</span>

          <div>
            <strong>Some live data could not be refreshed.</strong>

            <small>
              Previous successful values are still being displayed for{" "}
              {allFailedApiKeys.length} dashboard section
              {allFailedApiKeys.length > 1 ? "s" : ""}.
            </small>
          </div>
        </div>
      )}

      {/* =========================
          KPI CARDS
      ========================== */}

      <section className="compact-card-grid">
        {showInitialSkeleton
          ? Array.from({
              length: 6,
            }).map((_, index) => (
              <SkeletonCard key={`dashboard-skeleton-${index}`} />
            ))
          : cards.map((card) => (
              <MetricCard
                key={card.key}
                card={card}
                filters={filters}
                loading={loading}
                isRefreshing={isRefreshing}
                selectedCardNameExecute={handleSelectedCard}
              />
            ))}
      </section>

      {/* =========================
          PRODUCTION DASHBOARD
      ========================== */}

      <div
        className={`dashboard-content-wrapper ${
          isRefreshing ? "is-background-refreshing" : ""
        }`}
      >
        {activeCardName === "PRODUCTION" && (
          <ProductionDashboard
            data={details.data.productionDashboard}
            loading={details.loading}
            filters={detailFilters}
            dataToday={dashboardData.productionToday}
          />
        )}

        {activeCardName === "MACHINE" && (
          <MachineUtilizationDashboard
            loading={details.loading}
            isRefreshing={isRefreshing}
            filters={detailFilters}
            data={details.data.machineUtilizationCompleteData}
          />
        )}
        {activeCardName === "OPERATOR" && (
          <OperatorUtilizationDashboard
            loading={details.loading}
            isRefreshing={isRefreshing}
            filters={detailFilters}
            data={details.data.operatorUtilization}
          />
        )}
      </div>
    </div>
  );
}

export default Dashboard;
